export const DEFAULT_DATATABLE_PAGE = 1;
export const DEFAULT_DATATABLE_PER_PAGE = 10;

export function parsePositiveInt(
  value: string | number | null | undefined
): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

export function parsePaginationFromSearch(search: string): {
  page?: number;
  perPage?: number;
} {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);

  return {
    page: parsePositiveInt(params.get('page')),
    perPage: parsePositiveInt(params.get('perPage'))
  };
}

export function resolvePersistedPagination(args: {
  persistPagination?: boolean;
  search?: string;
  snapshot?: { page?: number; perPage?: number } | null;
  defaultPage?: number;
  defaultPerPage?: number;
}): { page: number; perPage: number } {
  const defaultPage = args.defaultPage ?? DEFAULT_DATATABLE_PAGE;
  const defaultPerPage = args.defaultPerPage ?? DEFAULT_DATATABLE_PER_PAGE;

  if (!args.persistPagination) {
    return { page: defaultPage, perPage: defaultPerPage };
  }

  const fromUrl = parsePaginationFromSearch(args.search ?? '');

  return {
    page: fromUrl.page ?? parsePositiveInt(args.snapshot?.page) ?? defaultPage,
    perPage:
      fromUrl.perPage ??
      parsePositiveInt(args.snapshot?.perPage) ??
      defaultPerPage
  };
}

export function lastPageFromPagination(pagination: {
  page: number;
  perPage: number;
  total: number;
  lastPage?: number;
}): number {
  if (pagination.lastPage != null && pagination.lastPage >= 0) {
    return Math.max(pagination.lastPage, 1);
  }

  if (pagination.total <= 0 || pagination.perPage <= 0) return 1;

  return Math.max(1, Math.ceil(pagination.total / pagination.perPage));
}

export function clampRequestedPage(
  page: number,
  lastPage: number,
  total: number
): number {
  if (total <= 0 || lastPage < 1) return 1;
  if (page > lastPage) return lastPage;
  if (page < 1) return 1;
  return page;
}

export function writeDatatablePaginationToUrl(args: {
  page: number;
  perPage: number;
  pathname?: string;
  search?: string;
}): string {
  const pathname = args.pathname ?? window.location.pathname;
  const params = new URLSearchParams(args.search ?? window.location.search);
  params.set('page', String(args.page));
  params.set('perPage', String(args.perPage));
  const query = params.toString();
  const nextUrl = query ? `${pathname}?${query}` : pathname;
  window.history.replaceState({}, document.title, nextUrl);
  return nextUrl;
}
